import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from "@/components/ui/table";
import { Send, Mail, Download, Check, Clock, AlertTriangle, Star, FileText } from 'lucide-react';
import { toast } from 'sonner';

const suppliers = [
  { id: 1, name: 'OpenTrolley', contact: 'Mr David Lim', email: 'david.lim@opentrolley.com', invited: true, quotesReceived: 194, status: 'complete' },
  { id: 2, name: 'Times Books', contact: 'Ms Sarah Lee', email: 'sarah@timesbooks.com.sg', invited: true, quotesReceived: 194, status: 'complete' },
  { id: 3, name: 'Popular Books', contact: 'Mr James Wong', email: 'james@popular.com.sg', invited: true, quotesReceived: 188, status: 'partial' },
  { id: 4, name: 'Grassroots', contact: 'Mr Muthu Raman', email: 'muthu@grassroots.com.sg', invited: true, quotesReceived: 56, status: 'mtl_only' },
  { id: 5, name: 'MPH Distributors', contact: 'Ms Rachel Tan', email: 'rachel@mph.com.sg', invited: false, quotesReceived: 0, status: 'not_invited' },
  { id: 6, name: 'Kinokuniya', contact: 'Mr Tanaka Hiroshi', email: 'tanaka@kinokuniya.com.sg', invited: false, quotesReceived: 0, status: 'not_invited' },
];

const quotesReceived = [
  { id: 1, title: 'The Wonders of Coding', openTrolley: 18.50, times: 18.80, popular: 19.00, grassroots: null, lowest: 'OpenTrolley', recommended: 'OpenTrolley', stock: { openTrolley: 500, times: 450, popular: 420 } },
  { id: 2, title: 'Climate Change & You', openTrolley: 14.20, times: 14.50, popular: 14.80, grassroots: null, lowest: 'OpenTrolley', recommended: 'OpenTrolley', stock: { openTrolley: 600, times: 380, popular: 420 } },
  { id: 3, title: 'Singapore Stories Vol 3', openTrolley: 22.50, times: 22.80, popular: 23.00, grassroots: null, lowest: 'OpenTrolley', recommended: 'OpenTrolley', stock: { openTrolley: 400, times: 350, popular: 380 } },
  { id: 4, title: 'Between Two Worlds', openTrolley: 16.80, times: 16.50, popular: 17.20, grassroots: null, lowest: 'Times', recommended: 'Times', stock: { openTrolley: 320, times: 400, popular: 280 } },
  { id: 5, title: 'Semangat!', openTrolley: 17.00, times: 17.20, popular: null, grassroots: 16.80, lowest: 'Grassroots', recommended: 'Grassroots', stock: { openTrolley: 200, times: 180, grassroots: 250 } },
  { id: 6, title: 'Nam Kathaigal', openTrolley: 15.80, times: 16.00, popular: null, grassroots: 15.40, lowest: 'Grassroots', recommended: 'Grassroots', stock: { openTrolley: 150, times: 120, grassroots: 180 } },
];

const justifications = [
  { id: 1, title: 'Secondary Science Digest', recommended: 'Popular Books', price: 15.20, lowestSupplier: 'Times', lowestPrice: 14.80, reason: 'Times stock insufficient (80 units). Popular has 420 units available.', status: 'documented' },
  { id: 2, title: 'Tamil Literature Anthology', recommended: 'Grassroots', price: 14.00, lowestSupplier: 'Grassroots', lowestPrice: 14.00, reason: 'Sole MTL-TL specialist. Reprint confirmed for stock shortage.', status: 'documented' },
  { id: 3, title: 'Math Made Fun', recommended: 'EPH', price: 13.50, lowestSupplier: 'EPH', lowestPrice: 13.50, reason: 'Publisher direct. Best price and fastest delivery.', status: 'documented' },
];

export default function CivicaQuoteInvitations() {
  const [selectedSuppliers, setSelectedSuppliers] = useState(['1', '2', '3', '4']);

  const handleSendInvitations = () => {
    const count = selectedSuppliers.length;
    toast.success(`Invitations sent to ${count} suppliers for 194 titles`);
  };

  const handleToggleSupplier = (id) => {
    if (selectedSuppliers.includes(id)) {
      setSelectedSuppliers(selectedSuppliers.filter(s => s !== id));
    } else {
      setSelectedSuppliers([...selectedSuppliers, id]);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-3">
            <Send className="w-7 h-7 text-[#2E5F8A]" />
            Quote Invitation & Comparison — CDS Cycle 1 2028
          </h1>
          <p className="text-slate-500 mt-1">194 approved titles | 4 suppliers invited | Response deadline: 5 working days</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-emerald-50">
          <CardContent className="pt-6 text-center">
            <div className="text-2xl font-bold text-emerald-700">194</div>
            <div className="text-sm text-emerald-600">Titles for Quoting</div>
          </CardContent>
        </Card>
        <Card className="bg-blue-50">
          <CardContent className="pt-6 text-center">
            <div className="text-2xl font-bold text-blue-700">4</div>
            <div className="text-sm text-blue-600">Suppliers Invited</div>
          </CardContent>
        </Card>
        <Card className="bg-emerald-50">
          <CardContent className="pt-6 text-center">
            <div className="text-2xl font-bold text-emerald-700">632</div>
            <div className="text-sm text-emerald-600">Quotes Received</div>
          </CardContent>
        </Card>
        <Card className="bg-amber-50">
          <CardContent className="pt-6 text-center">
            <div className="text-2xl font-bold text-amber-700">38</div>
            <div className="text-sm text-amber-600">Need Justification</div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="send">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="send" data-testid="tab-send">Send Invitations</TabsTrigger>
          <TabsTrigger value="received" data-testid="tab-received">Quotes Received (632)</TabsTrigger>
          <TabsTrigger value="compare" data-testid="tab-compare">Compare & Recommend</TabsTrigger>
          <TabsTrigger value="justifications" data-testid="tab-justifications">Justifications (38)</TabsTrigger>
          <TabsTrigger value="submit" data-testid="tab-submit">Submit to MOE HQ</TabsTrigger>
        </TabsList>

        {/* Send Invitations Tab */}
        <TabsContent value="send" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Send Quote Invitations</CardTitle>
              <CardDescription>Select suppliers to invite for quoting on 194 approved titles</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">Select</TableHead>
                      <TableHead>Supplier</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Invitation Status</TableHead>
                      <TableHead>Quotes Received</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {suppliers.map((supplier) => (
                      <TableRow key={supplier.id} className="table-row-striped">
                        <TableCell>
                          <Checkbox 
                            checked={selectedSuppliers.includes(String(supplier.id))}
                            onCheckedChange={() => handleToggleSupplier(String(supplier.id))}
                          />
                        </TableCell>
                        <TableCell className="font-medium">{supplier.name}</TableCell>
                        <TableCell>{supplier.contact}</TableCell>
                        <TableCell className="text-sm text-slate-500">{supplier.email}</TableCell>
                        <TableCell>
                          {supplier.invited ? (
                            <Badge className="bg-emerald-50 text-emerald-700">Invited ✓</Badge>
                          ) : (
                            <Badge variant="outline">Not Invited</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {supplier.status === 'complete' && (
                            <Badge className="bg-emerald-50 text-emerald-700">{supplier.quotesReceived} ✓</Badge>
                          )}
                          {supplier.status === 'partial' && (
                            <Badge className="bg-amber-50 text-amber-700">{supplier.quotesReceived}/194</Badge>
                          )}
                          {supplier.status === 'mtl_only' && (
                            <Badge className="bg-blue-50 text-blue-700">{supplier.quotesReceived} (MTL)</Badge>
                          )}
                          {supplier.status === 'not_invited' && (
                            <span className="text-slate-400">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-600 mb-2"><strong>Invitation Template Preview:</strong></p>
                  <p className="text-sm text-slate-500">
                    "Dear [Supplier], Civica invites you to submit quotes for 194 titles for CDS Cycle 1 2028. 
                    Please submit your best prices and stock availability within 5 working days. 
                    Full title list attached."
                  </p>
                </div>

                <Button onClick={handleSendInvitations} className="bg-[#2E5F8A] hover:bg-[#254B73]" data-testid="send-invitations-btn">
                  <Mail className="w-4 h-4 mr-2" />
                  Send Invitations to {selectedSuppliers.length} Suppliers
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Quotes Received Tab */}
        <TabsContent value="received" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="font-heading">Quotes Received</CardTitle>
                <CardDescription>632 quotes from 4 suppliers</CardDescription>
              </div>
              <Button variant="outline" size="sm">
                <Download className="w-4 h-4 mr-2" />
                Export All Quotes
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead className="text-center">OpenTrolley</TableHead>
                    <TableHead className="text-center">Times</TableHead>
                    <TableHead className="text-center">Popular</TableHead>
                    <TableHead className="text-center">Grassroots</TableHead>
                    <TableHead>Lowest</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {quotesReceived.map((quote) => (
                    <TableRow key={quote.id} className="table-row-striped">
                      <TableCell className="font-medium">{quote.title}</TableCell>
                      <TableCell className={`text-center ${quote.lowest === 'OpenTrolley' ? 'bg-emerald-50 font-semibold text-emerald-700' : ''}`}>
                        {quote.openTrolley ? `$${quote.openTrolley.toFixed(2)}` : '—'}
                      </TableCell>
                      <TableCell className={`text-center ${quote.lowest === 'Times' ? 'bg-emerald-50 font-semibold text-emerald-700' : ''}`}>
                        {quote.times ? `$${quote.times.toFixed(2)}` : '—'}
                      </TableCell>
                      <TableCell className={`text-center ${quote.lowest === 'Popular' ? 'bg-emerald-50 font-semibold text-emerald-700' : ''}`}>
                        {quote.popular ? `$${quote.popular.toFixed(2)}` : '—'}
                      </TableCell>
                      <TableCell className={`text-center ${quote.lowest === 'Grassroots' ? 'bg-emerald-50 font-semibold text-emerald-700' : ''}`}>
                        {quote.grassroots ? `$${quote.grassroots.toFixed(2)}` : '—'}
                      </TableCell>
                      <TableCell>
                        <Badge className="bg-emerald-100 text-emerald-700">{quote.lowest}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <p className="text-sm text-slate-500 mt-4 text-center">Showing 6 of 194 titles</p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Compare & Recommend Tab */}
        <TabsContent value="compare" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Compare & Recommend</CardTitle>
              <CardDescription>Auto-select lowest quotes with adequate stock. Flag exceptions for justification.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Sample detailed comparison */}
                <div className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-lg">"The Wonders of Coding"</h4>
                    <Badge className="bg-emerald-100 text-emerald-700">
                      <Star className="w-3 h-3 mr-1" /> Auto-recommended
                    </Badge>
                  </div>
                  <div className="grid grid-cols-4 gap-4">
                    <div className="p-3 bg-emerald-50 rounded-lg border-2 border-emerald-500">
                      <p className="text-sm font-medium">OpenTrolley</p>
                      <p className="text-xl font-bold text-emerald-700">$18.50</p>
                      <p className="text-xs text-slate-500 mt-1">Stock: 500</p>
                      <Badge className="mt-2 bg-emerald-100 text-emerald-700">
                        <Check className="w-3 h-3 mr-1" /> RECOMMENDED
                      </Badge>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-sm font-medium">Times Books</p>
                      <p className="text-xl font-bold">$18.80</p>
                      <p className="text-xs text-slate-500 mt-1">Stock: 450</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-sm font-medium">Popular</p>
                      <p className="text-xl font-bold">$19.00</p>
                      <p className="text-xs text-slate-500 mt-1">Stock: 420</p>
                    </div>
                    <div className="p-3 bg-slate-100 rounded-lg opacity-50">
                      <p className="text-sm font-medium">Grassroots</p>
                      <p className="text-xl font-bold text-slate-400">N/A</p>
                      <p className="text-xs text-slate-400 mt-1">Not quoted</p>
                    </div>
                  </div>
                </div>

                {/* Another comparison with exception */}
                <div className="p-4 border rounded-lg border-amber-200 bg-amber-50/30">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-lg">"Secondary Science Digest"</h4>
                    <Badge className="bg-amber-100 text-amber-700">
                      <AlertTriangle className="w-3 h-3 mr-1" /> Needs Justification
                    </Badge>
                  </div>
                  <div className="grid grid-cols-4 gap-4">
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-sm font-medium">OpenTrolley</p>
                      <p className="text-xl font-bold">$15.40</p>
                      <p className="text-xs text-slate-500 mt-1">Stock: 380</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg border-2 border-amber-300">
                      <p className="text-sm font-medium">Times Books</p>
                      <p className="text-xl font-bold text-amber-600">$14.80</p>
                      <p className="text-xs text-red-500 mt-1">Stock: 80 ⚠️</p>
                      <Badge className="mt-2 bg-amber-100 text-amber-700">Lowest but low stock</Badge>
                    </div>
                    <div className="p-3 bg-emerald-50 rounded-lg border-2 border-emerald-500">
                      <p className="text-sm font-medium">Popular</p>
                      <p className="text-xl font-bold text-emerald-700">$15.20</p>
                      <p className="text-xs text-slate-500 mt-1">Stock: 420</p>
                      <Badge className="mt-2 bg-emerald-100 text-emerald-700">
                        <Check className="w-3 h-3 mr-1" /> RECOMMENDED
                      </Badge>
                    </div>
                    <div className="p-3 bg-slate-100 rounded-lg opacity-50">
                      <p className="text-sm font-medium">Grassroots</p>
                      <p className="text-xl font-bold text-slate-400">N/A</p>
                      <p className="text-xs text-slate-400 mt-1">Not quoted</p>
                    </div>
                  </div>
                  <div className="mt-3 p-3 bg-white rounded border">
                    <p className="text-sm font-medium text-slate-700">Justification Required:</p>
                    <Textarea 
                      placeholder="Times stock (80 units) insufficient for ~287 schools. Recommending Popular at $15.20 with adequate stock (420)."
                      className="mt-2"
                    />
                    <Button size="sm" className="mt-2 bg-[#2E5F8A]">Save Justification</Button>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button className="bg-[#2E5F8A] hover:bg-[#254B73]">
                    <Check className="w-4 h-4 mr-2" />
                    Auto-Select All Lowest (156 titles)
                  </Button>
                  <Button variant="outline">
                    Review 38 Exceptions
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Justifications Tab */}
        <TabsContent value="justifications" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Justifications for Non-Lowest Recommendations</CardTitle>
              <CardDescription>38 titles require documented justification before MOE submission</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Recommended</TableHead>
                    <TableHead>Rec. Price</TableHead>
                    <TableHead>Lowest Supplier</TableHead>
                    <TableHead>Lowest Price</TableHead>
                    <TableHead>Justification</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {justifications.map((j) => (
                    <TableRow key={j.id} className="table-row-striped">
                      <TableCell className="font-medium">{j.title}</TableCell>
                      <TableCell>{j.recommended}</TableCell>
                      <TableCell>${j.price.toFixed(2)}</TableCell>
                      <TableCell>{j.lowestSupplier}</TableCell>
                      <TableCell>${j.lowestPrice.toFixed(2)}</TableCell>
                      <TableCell className="max-w-xs text-sm text-slate-600">{j.reason}</TableCell>
                      <TableCell>
                        <Badge className="bg-emerald-50 text-emerald-700">Documented ✓</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <p className="text-sm text-slate-500 mt-4 text-center">Showing 3 of 38 justifications required</p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Submit to MOE HQ Tab */}
        <TabsContent value="submit" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Submit Recommendations to MOE HQ</CardTitle>
              <CardDescription>Final submission of all supplier recommendations with supporting evidence</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-emerald-50 rounded-lg">
                    <div className="text-2xl font-bold text-emerald-700">156</div>
                    <div className="text-sm text-emerald-600">Ready for Submission</div>
                    <p className="text-xs text-emerald-500 mt-1">Lowest price with adequate stock</p>
                  </div>
                  <div className="p-4 bg-amber-50 rounded-lg">
                    <div className="text-2xl font-bold text-amber-700">38</div>
                    <div className="text-sm text-amber-600">With Justifications</div>
                    <p className="text-xs text-amber-500 mt-1">All documented and ready</p>
                  </div>
                </div>

                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium mb-3">Submission Package Includes:</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-emerald-600" />
                      <span>194 title recommendations with supplier & price</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-emerald-600" />
                      <span>632 supplier quotes (comparison spreadsheet)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-emerald-600" />
                      <span>38 documented justifications for non-lowest selections</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-emerald-600" />
                      <span>Stock availability confirmation from all suppliers</span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button 
                    className="bg-[#2E5F8A] hover:bg-[#254B73]"
                    onClick={() => toast.success('All 194 recommendations submitted to MOE HQ')}
                    data-testid="submit-to-moe-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Submit All 194 Recommendations to MOE HQ
                  </Button>
                  <Button variant="outline">
                    <Download className="w-4 h-4 mr-2" />
                    Download Evidence Package (ZIP)
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
